import type { CSSProperties } from 'react';
import { STRATEGY_CATALOG, clampFractalWings, strategyList, type StrategyKind } from '@termix/shared';
import { chipCopy, useI18n } from '../lib/i18n';
import MarginControls, { type ExchangeMarginConfig, type MarginMetrics, type MarginMode } from './MarginControls';

export type RunStatus = 'armed' | 'signals_only' | 'in_position' | 'stopped' | 'killed' | string | null;

type Props = {
  kind: StrategyKind;
  params: Record<string, unknown>;
  onlyProfitPct: string;
  onlyProfitEnabled: boolean;
  riskPct: string;
  riskMaxPct: string;
  running?: boolean;
  runStatus?: RunStatus;
  runKindLabel?: string | null;
  runSymbol?: string | null;
  hasKey?: boolean;
  strategyLabel?: string;
  onKind: (k: StrategyKind) => void;
  onParam: (key: string, value: unknown) => void;
  onOnlyProfitPct: (v: string) => void;
  onOnlyProfitEnabled: (v: boolean) => void;
  onRiskPct: (v: string) => void;
  onRiskMaxPct: (v: string) => void;
  onArm: () => void;
  onStop: () => void;
  rangeLabel?: string;
  rangeTradesCount?: number;
  marginMode?: MarginMode;
  leverage?: string;
  exchangeMargin?: ExchangeMarginConfig;
  marginBusy?: boolean;
  marginMetrics?: MarginMetrics | null;
  onMarginMode?: (m: MarginMode) => void;
  onLeverage?: (v: string) => void;
  onApplyMargin?: () => void;
  sizeWarn?: { text: string; level: 'warn' | 'info' } | null;
  runNotice?: string | null;
  /** How the strategy was last stopped (or soft-stop in progress). */
  stopMode?: 'leave' | 'close' | 'soft' | null;
  /** Live Book watch line (OB brick the chip waits for / taps). */
  bookHint?: string | null;
  /** Spot tab — hide short dial and futures margin block. */
  spotMode?: boolean;
};

export default function StrategyPanel({
  kind, params, onlyProfitPct, onlyProfitEnabled, riskPct, riskMaxPct,
  running, runStatus = null, runKindLabel = null, runSymbol = null,
  hasKey, strategyLabel,
  onKind, onParam, onOnlyProfitPct, onOnlyProfitEnabled, onRiskPct, onRiskMaxPct,
  onArm, onStop, rangeLabel, rangeTradesCount = 0,
  marginMode = 'cross', leverage = '10', exchangeMargin = null, marginBusy,
  marginMetrics = null,
  onMarginMode, onLeverage, onApplyMargin,
  sizeWarn = null, runNotice = null,
  stopMode = null,
  bookHint = null,
  spotMode = false,
}: Props) {
  const { t } = useI18n();
  const meta = STRATEGY_CATALOG[kind];
  const copy = chipCopy(t, kind);
  const softActive = Boolean(params?.stopAfterExit) || stopMode === 'soft';
  const live = (runStatus === 'armed' || runStatus === 'in_position' || runStatus === 'signals_only' || running)
    && !(runStatus === 'stopped' || runStatus === 'killed');
  const statusLabel = softActive && live
    ? t.stratSoftStopping
    : runStatus === 'in_position' ? t.stratInPosition
      : runStatus === 'signals_only' ? t.stratSignals
        : live ? t.stratRunning
          : stopMode === 'close' ? t.stratStoppedClosed
            : stopMode === 'leave' ? t.stratStoppedLeft
              : t.stratStopped;
  const statusColor = softActive && live ? 'var(--gold)'
    : runStatus === 'in_position' ? 'var(--gold)'
      : live ? 'var(--long)'
        : 'var(--muted)';
  const stopBtnLabel = softActive && live
    ? t.stopStrategySoftActive
    : t.stopStrategy;

  const longOn = params.longEnabled !== false;
  const shortOn = spotMode ? false : params.shortEnabled !== false;
  const longDens = Number(params.longTradeDensity ?? meta.defaults.longTradeDensity ?? 50);
  const shortDens = Number(params.shortTradeDensity ?? meta.defaults.shortTradeDensity ?? 24);
  const maxOrders = Number(params.maxOrders ?? meta.defaults.maxOrders ?? 3);
  const fractalWings = clampFractalWings(
    params.fractalWings ?? meta.defaults.fractalWings,
    kind === 'hideep' ? 1 : 2,
  );
  const risk = Number(riskPct) || 2;
  const minProfit = Number(onlyProfitPct) || 0.15;

  return (
    <div>
      <div style={{
        marginBottom: 10, padding: '10px 12px', borderRadius: 10,
        border: `1px solid ${live ? 'var(--long)' : 'var(--line)'}`,
        background: live ? 'rgba(61,186,122,0.1)' : 'var(--panel-2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: 99, background: statusColor,
            boxShadow: live ? `0 0 8px ${statusColor}` : undefined,
          }} />
          <span style={{ fontWeight: 700, color: statusColor, fontSize: 13 }}>{statusLabel}</span>
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text)', fontFamily: 'var(--mono)' }}>
          {live
            ? `${runKindLabel || strategyLabel || copy.name}${runSymbol ? ` · ${runSymbol}` : ''}`
            : t.stratStatusIdle}
        </div>
      </div>

      <div style={{
        fontSize: 12, color: 'var(--gold)', marginBottom: 6, fontWeight: 700,
        letterSpacing: '0.06em',
      }}>{t.strategiesTitle}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, lineHeight: 1.4 }}>
        {t.strategiesHint}
      </div>
      <select value={kind} onChange={(e) => onKind(e.target.value as StrategyKind)} style={selectStyle}>
        {strategyList().map((s) => (
          <option key={s.id} value={s.id}>{chipCopy(t, s.id).name}</option>
        ))}
      </select>

      <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: 'var(--panel-2)', border: '1px solid var(--line)' }}>
        <div style={{ color: 'var(--gold)', fontWeight: 700, marginBottom: 2, fontSize: 14 }}>{copy.name}</div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, lineHeight: 1.35 }}>{copy.short}</div>
        <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.45 }}>{copy.description}</div>
        {kind === 'order_book' && bookHint ? (
          <div style={{
            marginTop: 8, padding: '8px 10px', borderRadius: 8,
            border: '1px solid var(--line)', background: 'var(--panel)',
            fontSize: 12, color: 'var(--gold)', lineHeight: 1.4, fontFamily: 'var(--mono)',
          }}>
            {bookHint}
          </div>
        ) : null}
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
          {t.rangeLabel}: {rangeLabel || t.rangeNone}
        </div>
        {rangeTradesCount > 0 ? (
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--long)' }}>
            {t.rangeTrades} · {strategyLabel || copy.name}: {rangeTradesCount}
          </div>
        ) : (
          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--muted)', lineHeight: 1.4 }}>
            {rangeLabel ? t.rangeSketchEmpty : t.rangeDrawHint}
          </div>
        )}
      </div>

      {!spotMode && onMarginMode && onLeverage && onApplyMargin ? (
        <MarginControls
          mode={marginMode}
          leverage={leverage}
          exchange={exchangeMargin}
          busy={marginBusy}
          applyDisabled={!hasKey}
          metrics={marginMetrics}
          onMode={onMarginMode}
          onLeverage={onLeverage}
          onApply={onApplyMargin}
          labels={{
            title: t.margin,
            exchange: t.marginExchange,
            apply: t.marginApply,
            cross: t.cross,
            isolated: t.isolated,
            leverage: t.leverage,
            available: t.marginAvailable,
            used: t.marginUsed,
            maintenance: t.marginMaint,
            equity: t.marginEquity,
            effectiveLev: t.marginEffLev,
            ratio: t.marginRatio,
          }}
        />
      ) : null}

      {runNotice ? (
        <div style={{
          marginTop: 8, marginBottom: 4, padding: '10px 12px', borderRadius: 10,
          border: '1px solid var(--short)', background: 'rgba(226,91,91,0.12)',
          color: 'var(--short)', fontSize: 12, lineHeight: 1.4,
        }}>
          <b>{t.delistNoticeTitle}</b>
          <div style={{ marginTop: 4, color: 'var(--text)' }}>{runNotice}</div>
        </div>
      ) : null}

      {meta.simpleDesk ? (
        <div style={{ marginTop: 8 }}>
          {!live ? (
            <div style={{
              marginBottom: 8, padding: '8px 10px', borderRadius: 8,
              border: '1px solid var(--gold)', background: 'rgba(201,162,39,0.1)',
              color: 'var(--gold)', fontSize: 12, lineHeight: 1.4,
            }}>
              {t.stratDialNeedStart}
            </div>
          ) : null}
          <SideDial
            label={t.sideLong}
            enabled={longOn}
            density={longDens}
            onEnabled={(v) => onParam('longEnabled', v)}
            onDensity={(v) => onParam('longTradeDensity', v)}
            fewerLabel={t.tradesFewer}
            moreLabel={t.tradesMore}
            color="var(--long)"
          />
          {!spotMode ? (
            <SideDial
              label={t.sideShort}
              enabled={shortOn}
              density={shortDens}
              onEnabled={(v) => onParam('shortEnabled', v)}
              onDensity={(v) => onParam('shortTradeDensity', v)}
              fewerLabel={t.tradesFewer}
              moreLabel={t.tradesMore}
              color="var(--short)"
              hint={t.shortStricterHint}
            />
          ) : (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
              Spot · long only · risk % of deposit (≤98%)
            </div>
          )}

          {kind === 'hideep' ? (
            <>
              <label style={labelStyle} title={t.fractalWingsHint}>{t.fractalWings}</label>
              <div style={dialRow}>
                <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 36 }}>0</span>
                <input
                  type="range"
                  min={0}
                  max={5}
                  step={1}
                  value={fractalWings}
                  onChange={(e) => onParam('fractalWings', clampFractalWings(e.target.value, 1))}
                  style={rangeStyle}
                />
                <span style={dialValue}>{fractalWings === 0 ? t.fractalOff : String(fractalWings)}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{t.fractalWingsHint}</div>
            </>
          ) : null}

          <label style={labelStyle}>{t.maxClips}</label>
          <input
            type="number"
            min={1}
            max={12}
            step={1}
            value={maxOrders}
            onChange={(e) => onParam('maxOrders', Math.max(1, Number(e.target.value) || 1))}
            style={inputStyle}
          />

          <label style={labelStyle}>{t.riskPerOrder}</label>
          <div style={dialRow}>
            <input
              type="range"
              min={0.5}
              max={20}
              step={0.5}
              value={risk}
              onChange={(e) => onRiskPct(e.target.value)}
              style={rangeStyle}
            />
            <span style={dialValue}>{risk.toFixed(1)}%</span>
          </div>

          <label style={labelStyle} title={t.onlyProfitHint}>{t.onlyProfit}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 8, alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={onlyProfitEnabled} onChange={(e) => onOnlyProfitEnabled(e.target.checked)} />
              {t.on}
            </label>
            <input
              type="range"
              min={0.05}
              max={1.5}
              step={0.05}
              value={minProfit}
              disabled={!onlyProfitEnabled}
              onChange={(e) => onOnlyProfitPct(e.target.value)}
              style={{ ...rangeStyle, opacity: onlyProfitEnabled ? 1 : 0.4 }}
            />
            <span style={dialValue}>{minProfit.toFixed(2)}%</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{t.onlyProfitHint}</div>
        </div>
      ) : (
        <>
          <label style={labelStyle} title={t.riskPerOrder}>{t.riskPerOrder}</label>
          <input type="number" min={0.1} max={100} step={0.1} value={riskPct} onChange={(e) => onRiskPct(e.target.value)} style={inputStyle} />
          <label style={labelStyle} title={t.riskMax}>{t.riskMax}</label>
          <input type="number" min={0.1} max={100} step={0.1} value={riskMaxPct} onChange={(e) => onRiskMaxPct(e.target.value)} style={inputStyle} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
            {meta.fields.map((f) => (
              <div key={f.key} style={f.type === 'boolean' ? { gridColumn: '1 / -1' } : undefined}>
                <label style={labelStyle}>{f.label}{f.hint ? ` · ${f.hint}` : ''}</label>
                {f.type === 'boolean' ? (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(params[f.key] ?? meta.defaults[f.key])}
                      onChange={(e) => onParam(f.key, e.target.checked)}
                    />
                    {t.on}
                  </label>
                ) : f.type === 'select' ? (
                  <select
                    value={String(params[f.key] ?? meta.defaults[f.key] ?? '')}
                    onChange={(e) => onParam(f.key, e.target.value)}
                    style={selectStyle}
                  >
                    {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input
                    type="number"
                    step={f.step ?? 0.1}
                    min={f.min}
                    max={f.max}
                    value={Number(params[f.key] ?? meta.defaults[f.key] ?? 0)}
                    onChange={(e) => onParam(f.key, Number(e.target.value))}
                    style={inputStyle}
                  />
                )}
              </div>
            ))}
          </div>
          <label style={labelStyle} title={t.onlyProfitHint}>{t.onlyProfit}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={onlyProfitEnabled} onChange={(e) => onOnlyProfitEnabled(e.target.checked)} />
              {t.on}
            </label>
            <input value={onlyProfitPct} onChange={(e) => onOnlyProfitPct(e.target.value)} style={inputStyle} title={t.onlyProfitHint} />
          </div>
        </>
      )}

      {sizeWarn ? (
        <div style={{
          marginTop: 8, padding: '8px 10px', borderRadius: 8,
          border: `1px solid ${sizeWarn.level === 'warn' ? 'var(--gold)' : 'var(--line)'}`,
          background: sizeWarn.level === 'warn' ? 'rgba(201,162,39,0.1)' : 'var(--panel-2)',
          color: sizeWarn.level === 'warn' ? 'var(--gold)' : 'var(--muted)',
          fontSize: 11, lineHeight: 1.4,
        }}>
          {sizeWarn.text}
        </div>
      ) : null}

      {!hasKey ? (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--short)' }}>
          {t.needKeyStrategy}
        </div>
      ) : null}

      {live ? (
        <button
          onClick={onStop}
          title={softActive ? t.stopModeSoftHint : t.stopDialogHint}
          style={{ ...dangerBtn, marginTop: 12 }}
        >
          {stopBtnLabel}
        </button>
      ) : (
        <>
          {stopMode ? (
            <div style={{
              marginTop: 10, fontSize: 11, color: 'var(--muted)', lineHeight: 1.35,
              padding: '6px 8px', borderRadius: 8, border: '1px solid var(--line)',
              background: 'var(--panel-2)',
            }}>
              {stopMode === 'close' ? t.stratStoppedClosed
                : stopMode === 'leave' ? t.stratStoppedLeft
                  : t.stratSoftStopping}
            </div>
          ) : null}
          <button onClick={onArm} style={{ ...primaryBtn, marginTop: 12, opacity: hasKey ? 1 : 0.7 }}>
            {t.startStrategy}
          </button>
        </>
      )}
    </div>
  );
}

function SideDial(props: {
  label: string;
  enabled: boolean;
  density: number;
  onEnabled: (v: boolean) => void;
  onDensity: (v: number) => void;
  fewerLabel: string;
  moreLabel: string;
  color: string;
  hint?: string;
}) {
  return (
    <div style={{
      marginTop: 10, padding: 10, borderRadius: 10,
      border: `1px solid ${props.enabled ? props.color : 'var(--line)'}`,
      background: props.enabled ? 'rgba(255,255,255,0.02)' : 'var(--panel-2)',
      opacity: props.enabled ? 1 : 0.72,
    }}>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, fontWeight: 600, color: props.color }}>
        <input type="checkbox" checked={props.enabled} onChange={(e) => props.onEnabled(e.target.checked)} />
        {props.label}
      </label>
      {props.hint ? <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{props.hint}</div> : null}
      <div style={{ ...dialRow, marginTop: 8, opacity: props.enabled ? 1 : 0.45 }}>
        <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 52 }}>{props.fewerLabel}</span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          disabled={!props.enabled}
          value={props.density}
          onChange={(e) => props.onDensity(Number(e.target.value))}
          style={rangeStyle}
        />
        <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 52, textAlign: 'right' }}>{props.moreLabel}</span>
      </div>
    </div>
  );
}

const inputStyle: CSSProperties = { width: '100%', marginTop: 4, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--panel-2)', color: 'var(--text)' };
const selectStyle: CSSProperties = { ...inputStyle };
const labelStyle: CSSProperties = { display: 'block', marginTop: 10, fontSize: 12, color: 'var(--muted)' };
const dialRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 };
const dialValue: CSSProperties = { fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--gold)', minWidth: 48, textAlign: 'right' };
const rangeStyle: CSSProperties = { width: '100%', accentColor: '#c9a227' };
const primaryBtn: CSSProperties = { width: '100%', padding: 12, borderRadius: 10, border: 'none', background: 'linear-gradient(180deg,#dfb83a,var(--gold))', color: '#111', fontWeight: 700, cursor: 'pointer' };
const dangerBtn: CSSProperties = { width: '100%', padding: 12, borderRadius: 10, border: '1px solid var(--short)', background: 'rgba(226,91,91,0.15)', color: 'var(--short)', fontWeight: 700, cursor: 'pointer' };
